import { defineComponent } from "vue";
import { Container } from "aurelia-dependency-injection";
import useVuelidate from "@vuelidate/core";
import { required } from "@vuelidate/validators";

import { ToastrService } from "../../../../../../services/toastr.service";
import { ReferralService } from "../../../../../../services/referral-service";
import { AssetsService } from "../../../../../../services/assets-service";
import { TaskService } from "../../../../../../services/task.service";
import { BranchesService } from "../../../../../../services/branches-service";
import { DocumentTypeCategory } from "../../../../../../enums/enums";
import { formatDate } from "../../../../../../common/vue-helpers/modifiers/value-modifier";
import { IBranchResponse } from "../../../../../../interfaces/i-branch";
import { IGetDocumentTypeLean } from "../../../../../../resources-vue/vue-interfaces/i-document-type";
import { IUploadVisitDocumentResponse } from "../../../../../../resources-vue/vue-interfaces/i-asset";
import { ISaveTaskAttachmentRequest } from "../../../../../../resources-vue/vue-interfaces/i-task";
import { IEnumResponse } from "../../../../../../resources-vue/vue-interfaces/i-enum";

import TypeaheadInput from "../../../../../../resources-vue/vue-custom-elements/TypeaheadInput/TypeaheadInput.vue";
import FormSection from "../../../../../../resources-vue/vue-custom-elements/FormSection/FormSection.vue";

export default defineComponent({
    components: {
        TypeaheadInput,
        FormSection
    },

    props: {
        visitDetails: { type: Object },
        toggleUploadFile: { type: Function }
    },

    validations() {
        return {
            fileDetails: {
                uploadTypeId: { required }
            }
        };
    },

    data() {
        return {
            fileDetails: null as IUploadVisitDocumentResponse,
            uploadFile: false,
            isLoading: false,
            isSaving: false,
            uploading: false,
            searchedTypes: [] as IEnumResponse[],
            selectedUploadType: undefined as string,
            fileUploadErrorMessage: null as string,
            providerId: "",
            DocumentTypeCategoryEnum: DocumentTypeCategory,
            _taskService: null as TaskService,
            _assetsService: null as AssetsService,
            _toastrService: null as ToastrService,
            _branchesService: null as BranchesService,
            _referralService: null as ReferralService,
            v$: useVuelidate(),
            formatDate
        };
    },

    created() {
        this._branchesService = Container.instance.get(BranchesService);
        this._assetsService = Container.instance.get(AssetsService);
        this._toastrService = Container.instance.get(ToastrService);
        this._referralService = Container.instance.get(ReferralService);
        this._taskService = Container.instance.get(TaskService);
    },

    async mounted() {
        await this.setProvider();
        await this.loadSearchedTypes();
    },

    methods: {
        async loadSearchedTypes() {
            if (!this.visitDetails?.PatientId) {
                this.searchedTypes = [];
            }
            let category = Number(this.DocumentTypeCategoryEnum.enum.Patient);
            let searchedTypes: IGetDocumentTypeLean[] = await this._referralService.getDocumentTypesList({
                providerIds: [this.providerId],
                application: this.visitDetails?.Application,
                accountId: this.visitDetails?.AgencyId,
                category
            });
            if (searchedTypes) {
                this.searchedTypes = searchedTypes.map((documentType: IGetDocumentTypeLean) => ({
                    value: documentType.id,
                    name: documentType.type
                }));
            } else {
                this.searchedTypes = [];
            }
        },

        async setProvider() {
            const patientBranchId = this.visitDetails?.LocationId;
            const branches: IBranchResponse[] = await this._branchesService.getAllBranches();
            const patientBranch = branches.find((branch) => branch.id === patientBranchId);
            this.providerId = patientBranch?.providerId ?? "";
        },

        updateTypeId(id: string) {
            this.fileDetails.uploadTypeId = id;
        },

        removeAllFiles() {
            this.fileDetails = null;
        },

        uploadFilesChanged(event: any) {
            this.uploadDocuments(event.target.files[0]);
        },

        getFileIcon(type: string) {
            if (!type) {
                return "fa-file";
            }
            if (type.match("image.*")) {
                return "fa-file-image";
            } else if (type.includes("wordprocessing")) {
                return "fa-file-word";
            } else if (type.includes("spreadsheet")) {
                return "fa-file-excel";
            } else if (type.includes("pdf")) {
                return "fa-file-pdf";
            } else {
                return "fa-file";
            }
        },

        async uploadDocuments(file: any) {
            if (file) {
                try {
                    this.fileUploadErrorMessage = null;
                    this.uploading = true;
                    const formData = new FormData();
                    formData.append(`attachment`, file);
                    let payload = {
                        agencyId: this.visitDetails.AgencyId,
                        application: this.visitDetails.Application
                    };
                    formData.append("request", JSON.stringify(payload));
                    const response: IUploadVisitDocumentResponse[] =
                        await this._assetsService.uploadVisitDocument(formData);
                    this.fileDetails = { ...this.fileDetails, ...response[0] };
                } catch (error) {
                    console.error(error);
                    await this.showUploadError();
                } finally {
                    this.uploading = false;
                }
            } else {
                this.uploading = false;
                this._toastrService.error({
                    title: `Error`,
                    message: `Please fill required Fields`
                });
            }
        },

        async saveAttachment() {
            const valid = await this.v$.$validate();
            if (!valid) {
                return;
            }
            if (!this.fileDetails) {
                this._toastrService.error({
                    title: "Error",
                    message: "File is required."
                });
                return;
            }
            try {
                const payload: ISaveTaskAttachmentRequest = {
                    AgencyId: this.visitDetails?.AgencyId,
                    Application: this.visitDetails?.Application,
                    TaskId: this.visitDetails?.Id,
                    AssetId: this.fileDetails?.assetId,
                    DocumentName: this.fileDetails?.name,
                    UploadType: this.fileDetails?.uploadTypeId
                };
                this.isSaving = true;
                const response = await this._taskService.saveTaskAttachement(payload);
                if (response.isSuccess) {
                    this._toastrService.success({
                        title: "Success",
                        message: "Attachment added successfully"
                    });
                } else {
                    this._toastrService.error({
                        title: "Error",
                        message: response.message || "There was a problem saving attached files. Please try again."
                    });
                }
                this.isSaving = false;
                this.toggleUploadFile();
                this.$emit("attachmentsRefresh");
            } catch (error) {
                console.error(error);
                this.isSaving = false;
                this._toastrService.error({
                    title: "Error",
                    message: "There was a problem saving attached files. Please try again."
                });
            }
        },

        async showUploadError() {
            this.fileUploadErrorMessage = "Error Uploading File";
            this._toastrService.error({
                title: `Error`,
                message: `${this.fileUploadErrorMessage}. Please try again.`
            });
        },

        isFieldRequired(index: number) {
            return this.v$.$errors.find((error) => (error.$propertyPath === `fileDetails.uploadTypeId` ? true : false));
        }
    }
});
